import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import { useAuthStore } from '@/store/auth'
import { EyeIcon, EyeSlashIcon, ArrowRightIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const ease: [number, number, number, number] = [0.16, 1, 0.3, 1]

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const reduced = useReducedMotion()
  const m = (d: number) => reduced ? undefined : { duration: 0.7, delay: d, ease }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !password) {
      toast.error('Please fill in all fields')
      return
    }

    setLoading(true)
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username: email, password })
      })

      const data = await res.json()

      if (res.ok && (data.token || data.data?.token)) {
        const token = data.token || data.data?.token
        const user = data.user || data.data?.user
        localStorage.setItem('jpdz_token', token)
        useAuthStore.setState({
          token,
          user,
          isAuthenticated: true,
          isInitializing: false
        })
        toast.success('Welcome back!')
        navigate('/chat')
      } else {
        const errorMsg = typeof data.error === 'string' ? data.error :
          typeof data.message === 'string' ? data.message : 'Login failed'
        toast.error(errorMsg)
      }
    } catch {
      toast.error('Network error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#211d18] text-[#eee8dc] flex items-center justify-center relative overflow-hidden">
      {/* Grid pattern */}
      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'linear-gradient(90deg, rgba(238,232,220,0.035) 1px, transparent 1px), linear-gradient(rgba(238,232,220,0.025) 1px, transparent 1px)', backgroundSize: '80px 80px' }} />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={m(0)}
        className="relative z-10 w-full max-w-[420px] px-6"
      >
        {/* Brand */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={m(0.05)} className="mb-14 text-center">
          <Link to="/" className="font-serif text-2xl text-[#eee8dc] transition-colors duration-200 hover:text-[#c8a88a]">
            Jpdz Labs
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={m(0.1)} className="mb-10">
          <p className="mb-3 font-body text-[11px] uppercase tracking-[0.3em] text-[#c8a88a]/50">Welcome back</p>
          <h1 className="font-serif text-[clamp(2.4rem,5vw,3.5rem)] font-medium leading-[0.9]">
            Sign in.
          </h1>
        </motion.div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-7">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={m(0.16)}>
            <label className="mb-2 block font-body text-[11px] uppercase tracking-[0.2em] text-[#eee8dc]/30">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border-b border-[#eee8dc]/10 bg-transparent px-0 py-3 font-body text-[16px] text-[#eee8dc] placeholder-[#eee8dc]/15 outline-none transition-colors duration-300 focus:border-[#c8a88a]/40"
              placeholder="you@example.com"
            />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={m(0.2)}>
            <label className="mb-2 block font-body text-[11px] uppercase tracking-[0.2em] text-[#eee8dc]/30">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border-b border-[#eee8dc]/10 bg-transparent px-0 py-3 pr-10 font-body text-[16px] text-[#eee8dc] placeholder-[#eee8dc]/15 outline-none transition-colors duration-300 focus:border-[#c8a88a]/40"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-0 top-1/2 -translate-y-1/2 p-1 text-[#eee8dc]/25 transition-colors hover:text-[#eee8dc]/50"
              >
                {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
              </button>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={m(0.24)} className="flex justify-end">
            <Link to="/forgot-password" className="font-body text-[13px] text-[#c8a88a]/40 transition-colors duration-200 hover:text-[#c8a88a]">
              Forgot password?
            </Link>
          </motion.div>

          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={m(0.28)}
            type="submit"
            disabled={loading}
            className="group flex w-full items-center justify-center gap-3 bg-[#6e1f1b] px-6 py-4 font-body text-[14px] font-medium tracking-wide text-[#eee8dc] transition-all duration-300 hover:bg-[#8a2822] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {loading ? (
              <motion.div
                className="h-4 w-4 border-2 border-[#eee8dc]/20 border-t-[#eee8dc] rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              />
            ) : (
              <>
                <span>Sign In</span>
                <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
              </>
            )}
          </motion.button>

          {/* Divider */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={m(0.32)} className="flex items-center gap-5">
            <div className="h-px flex-1 bg-[#eee8dc]/[0.06]" />
            <span className="font-body text-[10px] uppercase tracking-[0.2em] text-[#eee8dc]/15">or</span>
            <div className="h-px flex-1 bg-[#eee8dc]/[0.06]" />
          </motion.div>

          {/* Google */}
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={m(0.36)}
            type="button"
            onClick={async () => {
              try {
                const res = await fetch('/api/auth/google/login')
                const data = await res.json()
                if (data.auth_url) {
                  window.location.href = data.auth_url
                } else {
                  toast.error('Google login not configured')
                }
              } catch {
                toast.error('Google login unavailable')
              }
            }}
            className="group flex w-full items-center justify-center gap-3 border border-[#eee8dc]/[0.08] bg-transparent px-6 py-4 font-body text-[14px] text-[#eee8dc]/60 transition-all duration-300 hover:border-[#eee8dc]/20 hover:text-[#eee8dc]"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24">
              <path fill="#eee8dc" fillOpacity="0.5" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#eee8dc" fillOpacity="0.35" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#eee8dc" fillOpacity="0.25" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#eee8dc" fillOpacity="0.4" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            <span>Continue with Google</span>
          </motion.button>
        </form>

        {/* Footer */}
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={m(0.4)} className="mt-12 text-center font-body text-[14px] text-[#eee8dc]/25">
          Don't have an account?{' '}
          <Link to="/signup" className="text-[#c8a88a]/60 transition-colors duration-200 hover:text-[#c8a88a]">
            Sign up
          </Link>
        </motion.p>
      </motion.div>
    </div>
  )
}
